import React, { useContext, useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import InterfaceContext, { PublicConfigurationContext } from "./Context";
import { useLocalizedStrings } from "./Localization";
import { InfoDict } from "./Questions/Props";
import { useModularMarkdown } from "./Hooks/ModularMarkdown";
import * as v0 from "@aidkitorg/types/lib/survey";
import { ResizablePanelControlContext } from "./Components/ThreeColumnPage";
import { DropdownMenu, Modal, Button, Icon, useToast } from '@aidkitorg/component-library';
import { ReviewConfiguration } from '@aidkitorg/types/lib/translation/permissions';
import { AidKitLogo, SpacedSpinner } from "./Util";
import { usePost } from "./API";
import { RichText } from "@aidkitorg/types/lib/survey";
import { SubsurveyList } from "./Components/ApplicantSearch";
import { ApplicantHeaderCard } from "./DistroReview/ApplicantHeaderCard";
import { ApplicantPaymentPage } from "./Applicant/PaymentPage";

/** Component to display applicant subsurveys with sending options */
function ApplicantSubsurveys(props: { uid: string }) {
  const { toast } = useToast();
  const L = useLocalizedStrings();

  const [loading, setLoading] = useState(true);
  const [subsurveys, setSubsurveys] = useState<Array<{
    subsurveyNameTranslations: Record<string, string>;
    part: string;
    url?: string;
    send_link?: RichText;
  }>>([]);

  const getExposedSubsurveys = usePost('/applicant/get_exposed_subsurveys');

  useEffect(() => {
    (async () => {
      if (!props.uid) {
        setLoading(false);
        return;
      }

      try {
        const result = await getExposedSubsurveys({ applicant: props.uid });
        if (result && result.error) {
          toast({
            description: "Could not load subsurveys",
            variant: 'error'
          });
        } else {
          setSubsurveys(result?.subsurveys || []);
        }
      } catch (error) {
        toast({
          description: "Could not load subsurveys",
          variant: 'error'
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [props.uid]);

  if (loading) {
    return (
      <div className="p-4">
        <SpacedSpinner />
      </div>
    );
  }

  return (
    <div>
      {subsurveys.length > 0 ? (
        <div>
          <SubsurveyList uid={props.uid} subsurveys={subsurveys} showHeader={true} />
        </div>
      ) : (
        <p>{L.applicant_lookup.no_subsurveys_found}</p>
      )}
    </div>
  );
}

/** Circle indicator for review nav steps (number or check). Used as Button icon. */
export function StepCircleIndicator(props: {
  stepNumber: number;
  isActive: boolean;
  isCompleted: boolean;
  placement: 'left' | 'center';
}) {
  const { stepNumber, isActive, isCompleted, placement } = props;
  const wrapperClass = placement === 'left' ? 'flex-shrink-0 mr-2' : 'flex-shrink-0';

  if (isCompleted && !isActive) {
    return (
      <div className={wrapperClass} aria-hidden>
        <Icon
          name="CheckCircleIcon"
          type="solid"
          size="sm"
          color="var(--ak-identity-color-green-3)"
          role="decorative"
        />
      </div>
    );
  }

  return (
    <div
      className={`
        ${wrapperClass} w-[18px] h-[18px] rounded-full flex items-center justify-center text-xs
        ${isActive
      ? 'text-white'
      : 'text-gray-400 border border-gray-300'
    }
      `}
      style={isActive ? { backgroundColor: 'var(--ak-theme-color-button-primary-brand-background)' } : undefined}
      aria-hidden
    >
      <span className="text-xs leading-none flex items-center justify-center w-full h-full select-none tabular-nums">
        {stepNumber}
      </span>
    </div>
  );
}

interface ReviewWrapperProps {
  children: React.ReactNode;
}

export function ReviewWrapper({ children }: ReviewWrapperProps) {
  const panelContext = useContext(ResizablePanelControlContext);
  const publicConfig = useContext(PublicConfigurationContext);
  const [subSurveysModalOpen, setSubSurveysModalOpen] = useState(false);
  const [paymentsModalOpen, setPaymentsModalOpen] = useState(false);

  const L = useLocalizedStrings();

  const applicantFacingLogo = publicConfig.interface?.applicantFacingLogo?.url;
  const applicantFacingLogoWidth = publicConfig.interface?.applicantFacingLogo?.width || 150;

  // Only show the header when the review nav is visible (when the left nav is hidden)
  const showHeader = panelContext && !panelContext.isLeftNavVisible;

  // Extract uid and Action options from children props
  const uid = React.isValidElement(children) ? children.props.uid : undefined;
  const dropdownActions = React.isValidElement(children) ? children.props.review?.dropdownActions : undefined;
  const dropdownOptions = [
    dropdownActions?.viewOrResendSubsurveys && {
      label: L.review.view_or_resend_subsurveys,
      callback: () => {
        setSubSurveysModalOpen(true);
      }
    },
    dropdownActions?.seePayments && {
      label: L.review.see_payments,
      callback: () => {
        setPaymentsModalOpen(true);
      }
    }
  ].filter(Boolean) as Array<{ label: JSX.Element | string; callback: () => Promise<void> | void }>;

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-center py-2 pl-4 pr-2 border-b border-gray-200">
        <div className="flex-shrink-0">
          {showHeader && (
            <>
              {applicantFacingLogo ? (
                <img
                  src={applicantFacingLogo}
                  width={applicantFacingLogoWidth}
                  alt="Organization logo"
                  className="max-h-12"
                />
              ) : (
                <AidKitLogo width={150} />
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {dropdownOptions.length !== 0 &&
            <DropdownMenu label={L.review.actions} align="end" options={dropdownOptions} />
          }
          {panelContext && (
            <Button
              onClick={() => (panelContext.isRightNavVisible ? panelContext.hideRightNav() : panelContext.showRightNav())}
              title={panelContext.isRightNavVisible ? 'Close applicant info panel' : 'Open applicant info panel'}
              aria-label={panelContext.isRightNavVisible ? 'Close applicant info panel' : 'Open applicant info panel'}
              icon={panelContext.isRightNavVisible
                ? <Icon name='ChevronRightIcon' className="!h-4 !w-4 shrink-0" role='decorative' strokeWidth={1} />
                : <Icon name='ChevronLeftIcon' className="!h-4 !w-4 shrink-0" role='decorative' strokeWidth={1} />}
              iconPlacement="right"
              variant="secondary"
              size="md"
              className="!h-[32px] !rounded-md !border-gray-300"
            >
              {panelContext.isRightNavVisible ? 'Hide Tools' : 'Show Tools'}
            </Button>  
          )}
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        {children}
      </div>

      <Modal
        open={subSurveysModalOpen}
        onClose={setSubSurveysModalOpen}
        title=""
        size="lg"
      >
        <ApplicantSubsurveys uid={uid || ''} />
      </Modal>

      <Modal
        open={paymentsModalOpen}
        onClose={setPaymentsModalOpen}
        title=""
        size="fullscreen"
      >
        <ApplicantPaymentPage applicant={uid || ''} />
      </Modal>
    </div>
  );
}

export function showV2Review(reviewConfiguration?: ReviewConfiguration, userEmail?: string) {
  return !!reviewConfiguration
    && (!reviewConfiguration.showV2ToUsers ||
    (!!userEmail
    && reviewConfiguration.showV2ToUsers.includes(userEmail)));
}

export type ReviewDetails = ReviewConfiguration & {
  title: string | v0.Text;
}

export type ReviewNavStep = {
  id: string;
  title: string;
  completed?: boolean;
  progressed?: boolean;
  visitable?: boolean;
  visible: boolean;
  children?: JSX.Element;
}

export type ReviewNavProps = {
  steps: ReviewNavStep[];
  currentStep: number;
  setCurrentStep: (step: number) => void;
  sequential: boolean;
  title: v0.Text | string;
  blockedEditBanner?: v0.RichText | string,
  info?: InfoDict,
  reviewDetails?: ReviewDetails,
  uid?: string,
}

export function ReviewNav(props: ReviewNavProps) {
  const context = useContext(InterfaceContext);
  const L = useLocalizedStrings();
  const panelContext = useContext(ResizablePanelControlContext);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [headerCardCollapsed, setHeaderCardCollapsed] = useState(false);
  const headerRef = React.useRef<HTMLDivElement>(null);
  const history = useHistory();
  const location = useLocation();

  // Get the 'back' query parameter from the URL
  const searchParams = new URLSearchParams(location.search);
  const backUrl = searchParams.get('back');

  // TODO: determine if blocked edit banners need to be applied to review subsurveys. Is this a config that 
  // AEs use for reviews?
  const blockedBanner = props.blockedEditBanner
    ? typeof props.blockedEditBanner === 'object'
      ? props.blockedEditBanner[context.lang] || props.blockedEditBanner['en']
      : props.blockedEditBanner
    : undefined;
  const blockedBannerMarkdown = useModularMarkdown({
    content: blockedBanner || '',
    info: props.info || {},
  });

  const isLeftNavVisible = panelContext?.isLeftNavVisible ?? false;

  const title = typeof props.title === 'string'
    ? props.title
    : props.title[context.lang] || props.title['en'];

  // Observe header height changes for dynamic positioning of sidebar nav buttons
  useEffect(() => {
    if (headerRef.current && panelContext?.setHeaderHeight) {
      const resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          if (panelContext?.setHeaderHeight) {
            panelContext.setHeaderHeight(entry.contentRect.height);
          }
        }
      });

      resizeObserver.observe(headerRef.current);
      return () => resizeObserver.disconnect();
    }
  }, [panelContext]);

  return (
    <div className="flex h-full">
      {/* Vertical Sidebar Navigation */}
      <div className={`${isCollapsed ? 'w-15' : 'w-64'} border-r border-gray-200 bg-gray-50 overflow-y-auto flex-shrink-0 transition-all ${isLeftNavVisible ? 'hidden' : ''}`}>
        <div className={`p-2.5 flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
          {panelContext && !isCollapsed && (
            <Button
              variant="unstyled"
              onClick={() => {
                if (backUrl) {
                  history.push(backUrl);
                } else {
                  panelContext.showLeftNav();
                }
              }}
              className="p-1.5 rounded-md bg-gray-50 hover:bg-gray-200 text-gray-700 border border-gray-300 transition-colors"
              title="Show navigation"
              aria-label="Show navigation"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">{L.review.back}</span>
              </div>
            </Button>
          )}
          {!isCollapsed && (
            <Button
              variant="unstyled"
              onClick={() => setIsCollapsed(true)}
              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors flex justify-center"
              title="Collapse navigation"
              aria-label="Collapse navigation"
            >
              <Icon name="ChevronDoubleLeftIcon" type="solid" size="sm" role="decorative" />
            </Button>
          )}
          {isCollapsed && (
            <Button
              variant="unstyled"
              onClick={() => setIsCollapsed(false)}
              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors flex justify-center"
              title="Expand navigation"
              aria-label="Expand navigation"
            >
              <Icon name="ChevronDoubleRightIcon" type="solid" size="sm" role="decorative" />
            </Button>
          )}
        </div>
        {!isCollapsed && (
          <div className="py-2 px-4 text-xs uppercase text-gray-400 font-bold">{title}</div>
        )}
        <nav className={`p-2.5 ${isCollapsed ? 'flex flex-col items-center gap-2' : ''}`}>
          {props.steps.filter((s) => s.visible).map((step, filteredIndex) => {
            const originalIndex = props.steps.indexOf(step);
            const isActive = props.currentStep === originalIndex;
            const isCompleted = step.completed;
            const isVisitable = step.visitable;
            
            return (
              <Button
                key={`vertical-step-${originalIndex}`}
                onClick={() => {
                  if (isVisitable && !props.blockedEditBanner) {
                    props.setCurrentStep(originalIndex);
                  }
                }}
                variant={'unstyled'}
                disabled={!isVisitable || !!props.blockedEditBanner}
                style={{
                  justifyContent: !isCollapsed ? 'start' : 'center',
                  ...(isActive
                    ? {
                      color: 'var(--ak-theme-color-button-primary-brand-background)',
                    }
                    : {}),
                }}
                className={`
                  w-full text-left p-2 mb-1 mx-auto rounded-md transition-colors font-normal
                  ${isActive 
                ? 'border font-medium shadow-sm' 
                : isVisitable 
                  ? 'hover:bg-gray-100 text-gray-700' 
                  : 'text-gray-400 bg-gray-50'
              }
                  ${isCompleted && !isActive ? 'text-gray-600' : ''}
                `}
                icon={props.sequential ? (
                  <StepCircleIndicator
                    stepNumber={filteredIndex + 1}
                    isActive={isActive}
                    isCompleted={!!isCompleted}
                    placement={isCollapsed ? 'center' : 'left'}
                  />
                ) : undefined}
                iconPlacement={isCollapsed ? 'none' : 'left'}
              >
                {!isCollapsed && (
                  <span 
                    style={{
                      color: isActive ? 'var(--ak-theme-color-button-primary-brand-background)' : 'inherit',
                    }}
                    className={`text-sm font-normal ${isActive ? '' : 'text-gray-600'}`}
                  >
                    {step.title}
                  </span>
                )}
              </Button>
            );
          })}
        </nav>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-h-0">
        {props.reviewDetails && (
          <div ref={headerRef}>
            <ApplicantHeaderCard
              pinnedApplicantInfo={props.reviewDetails.pinnedApplicantInfo}
              uid={props.uid}
              applicationStartDate={props.reviewDetails.applicationStartDate}
              applicationState={props.reviewDetails.applicationState}
              isCollapsed={headerCardCollapsed}
              onCollapseChange={setHeaderCardCollapsed}
            />
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          {props.steps[props.currentStep]?.children}
        </div>
      </div>
    </div>
  );
}

